import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Target, TrendingUp, Users, Percent, ChevronDown, ChevronRight } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const money = (v) => Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

function AttBar({ value }) {
    const v = Math.max(0, Math.min(100, Number(value || 0)));
    const color = v >= 100 ? "bg-emerald-500" : v >= 60 ? "bg-amber-500" : "bg-red-500";
    return (
        <div className="flex items-center gap-2 min-w-[120px]">
            <div className="flex-1 h-2 rounded bg-secondary overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
            </div>
            <span className="text-xs font-medium tabular-nums w-12 text-right">{pct(value)}</span>
        </div>
    );
}

function Card({ icon: Icon, label, value }) {
    return (
        <div className="sc-card p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><Icon className="w-4 h-4" /></div>
            <div>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-semibold tracking-tight">{value}</div>
            </div>
        </div>
    );
}

export default function SalesQuota() {
    const [data, setData] = useState({ rows: [], years: [], branches: [], totals: {} });
    const [year, setYear] = useState("");
    const [branch, setBranch] = useState("");
    const [open, setOpen] = useState({});
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (year) params.year = year;
            if (branch) params.branch = branch;
            const { data } = await api.get("/crm/sales-quotas", { params });
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [year, branch]);

    // default to the latest year once known
    useEffect(() => {
        if (!year && data.years?.length) setYear(data.years[data.years.length - 1]);
    }, [data.years]);

    const grouped = useMemo(() => {
        const g = {};
        for (const r of data.rows || []) (g[r.branch] = g[r.branch] || []).push(r);
        return g;
    }, [data.rows]);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="sales-quota-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Sales Quota</h1>
                    <p className="text-sm text-muted-foreground mt-1">Per-agent quota vs actual sales, by branch and year.</p>
                </div>
                <div className="flex gap-2">
                    <select className="sc-input" value={year} onChange={(e) => setYear(e.target.value)} data-testid="quota-year">
                        {(data.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select className="sc-input" value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="quota-branch">
                        <option value="">All branches</option>
                        {(data.branches || []).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card icon={Target} label="Yearly Quota" value={`₱${money(data.totals?.yearly_quota)}`} />
                <Card icon={TrendingUp} label="Actual Sales" value={`₱${money(data.totals?.year_sales)}`} />
                <Card icon={Percent} label="Attainment" value={pct(data.totals?.attainment_pct)} />
                <Card icon={Users} label="Agents" value={data.totals?.agents ?? 0} />
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[900px]">
                        <thead>
                            <tr>
                                <th>Branch</th>
                                <th>Sales Agent</th>
                                <th>Manager</th>
                                <th className="text-right">Monthly Quota</th>
                                <th className="text-right">Yearly Quota</th>
                                <th className="text-right">Actual Sales</th>
                                <th>Attainment</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && (data.rows || []).length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No quota rows.</td></tr>}
                            {!loading && Object.entries(grouped).map(([br, rows]) => (
                                rows.map((r, i) => {
                                    const rid = r.id || `${br}-${i}`;
                                    const isOpen = !!open[rid];
                                    return (
                                        <>
                                            <tr key={rid} className="hover:bg-secondary/40">
                                                <td className="text-muted-foreground">{i === 0 ? br : ""}</td>
                                                <td className="font-medium">{r.sales_agent_name || "—"}{r.sales_lead_name ? <div className="text-[11px] text-muted-foreground">Lead: {r.sales_lead_name}</div> : null}</td>
                                                <td className="text-muted-foreground text-xs">{r.sales_manager_name || "—"}</td>
                                                <td className="sc-num">₱{money(r.monthly_quota)}</td>
                                                <td className="sc-num">₱{money(r.yearly_quota)}</td>
                                                <td className="sc-num font-semibold">₱{money(r.year_sales)}</td>
                                                <td><AttBar value={r.attainment_pct} /></td>
                                                <td>
                                                    <button onClick={() => setOpen((o) => ({ ...o, [rid]: !o[rid] }))} className="h-7 px-2 rounded border border-border text-xs inline-flex items-center gap-1 hover:bg-secondary" data-testid={`quota-months-${rid}`}>
                                                        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Months
                                                    </button>
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr key={`${rid}-m`} className="bg-secondary/20">
                                                    <td colSpan={8} className="p-3">
                                                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                                            {MONTHS.map((m) => {
                                                                const sales = Number((r.monthly_sales || {})[m] || 0);
                                                                const q = Number(r.monthly_quota || 0);
                                                                const p = q ? (sales / q * 100) : 0;
                                                                return (
                                                                    <div key={m} className="rounded border border-border p-2">
                                                                        <div className="text-[11px] text-muted-foreground">{m.slice(0, 3)}</div>
                                                                        <div className="text-xs font-semibold tabular-nums">₱{money(sales)}</div>
                                                                        <div className={`text-[10px] ${p >= 100 ? "text-emerald-600" : p >= 60 ? "text-amber-600" : "text-red-600"}`}>{pct(p)}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
